import { ReactNode } from "react";

import { AccessibilityProps } from "../../AccessibilityProps";
import { DataId } from "../../general";
import { withSubComponents } from "../../utilities/withSubComponents";
import { DataDropdownDataSource, OptionLabelProvider } from "../DataDropdown";
import { CustomReactSelectProps } from "../Dropdown/CustomReactSelectProps";
import { InputBaseProps } from "../InputBaseProps";
import { Placeholder } from "../Textbox/Placeholder";
import { SharedProps } from "../Textbox/SharedProps";
import { Field } from "./Field";
import { Input } from "./Input";

export interface SearchTextboxProps<TRow extends object>
  extends InputBaseProps<DataId | string | null>, Omit<SharedProps, "suffix">, Pick<AccessibilityProps, "aria-label"> {
  /**
   * The data source to use to retrieve dropdown items.
   */
  readonly dataSource: DataDropdownDataSource<TRow>;

  /**
   * The function to call returning the label for a particular row.
   */
  readonly optionLabelProvider: OptionLabelProvider<TRow>;

  /**
   * The field to search against. If remote, corresponds to a field alias. If not, corresponds to a dot path to the field on the row.
   */
  readonly searchField?: string;

  /**
   * Props for search input
   */
  readonly searchInputProps?: CustomReactSelectProps["searchInputProps"];

  /**
   * Indicates whether the dropdown is able to be cleared. If not set, defaults to `false`.
   */
  readonly clearable?: boolean;

  /**
   * Message to display when no options are returned from search.
   */
  readonly noOptionsMessage?: ReactNode;
}

/**
 * The `SearchTextbox` component sub components.
 */
export interface SearchTextboxSubComponents {
  /**
   * A React Hook Form compatible SearchTextbox Field component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the SearchTextbox which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

export const SearchTextbox = withSubComponents(
  {
    Field,
    Placeholder,
  } as SearchTextboxSubComponents,
  <TRow extends object>(props: SearchTextboxProps<TRow>) => {
    return <Input {...props} optionLabelProvider={props.optionLabelProvider as OptionLabelProvider<object>} />;
  },
);
